
import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTE_NOT_FOUND, ROUTE_PRODUCT_DETAILS, ROUTE_PRODUCT_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { getSuggestedSalePrice, isNullOrEmptyString, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import { editProduct, getActiveCategories, getEditProductDataById } from '../../services/ManagementService';
import noImage from '../../assets/No_Image_Available.jpg';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import FormTitle from '../../share-components/FormTitle';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';

const ProductEdit = () => {
    const { setLoading, appSettings, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const { productId } = useParams();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [colorName, setColorName] = useState('');
    const [additionalColorPrice, setAdditionalColorPrice] = useState('');
    const [colorList, setColorList] = useState([]);
    const [size, setSize] = useState('');
    const [additionalSizePrice, setAdditionalSizePrice] = useState('');
    const [sizeList, setSizeList] = useState([]);
    const [productData, setProductData] = useState(null);
    const [oldColorList, setOldColorList] = useState([]);
    const [oldSizeList, setOldSizeList] = useState([]);
    const [editedColorList, setEditedColorList] = useState([]);
    const [editedSizeList, setEditedSizeList] = useState([]);
    const [currentEditColorId, setCurrentEditColorId] = useState(null);
    const [currentEditSizeId, setCurrentEditSizeId] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [estimateProfit, setEstimateProfit] = useState(0);
    const [suggestedSalePrice, setSuggestedSalePrice] = useState(0);
    const [suggestedSalePercentage, setSuggestedSalePercentage] = useState(0);
    const [salePriceState, setSalePriceState] = useState(0);
    const [pricePerItemState, setPricePerItemState] = useState(0);
    const [formValue, setFormValue] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [categories, setCategories] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const fetchProductAndCategories = async () => {
        if (parseInt(productId) <= 0) {
            navigate(ROUTE_NOT_FOUND);
            return;
        }
        setLoading(true);
        try {
            const categoryList = await getActiveCategories();
            let sortedCategories = (categoryList || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategories(sortedCategories);
            const data = await getEditProductDataById(parseInt(productId) || '');
            if (data.deactivateFlag !== false) {
                navigate(ROUTE_PRODUCT_LIST);
                return;
            }
            setSalePriceState(data.salePrice);
            setPricePerItemState(Math.round((data?.totalPurchasedAmount / data?.totalPurchasedQuantity) || 0));
            setOldColorList(data.colorList || []);
            setOldSizeList(data.sizeList || []);
            setProductData(data);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("PRODUCT >> EDIT PRODUCT");
        fetchProductAndCategories();
    }, []);

    useEffect(() => {
        setEstimateProfit(Math.round((parseInt(salePriceState) || 0) - (parseInt(pricePerItemState) || 0)) || 0);
    }, [salePriceState, pricePerItemState]);

    useEffect(() => {
        const percentageSettings = appSettings?.suggestedSalePricePercentage;
        const suggested = getSuggestedSalePrice(pricePerItemState, percentageSettings);
        setSuggestedSalePrice(suggested?.suggestedPrice || 0);
        setSuggestedSalePercentage(suggested?.percentage || 0);
    }, [pricePerItemState, appSettings]);

    const getColorSizeMemoryData = (oldColorData, addedColorData, oldSizeData, addedSizeData) => {
        oldColorData = oldColorData || [];
        addedColorData = addedColorData || [];
        oldSizeData = oldSizeData || [];
        addedSizeData = addedSizeData || [];
        const newColorArray = [...oldColorData, ...addedColorData] || [];
        const newSizeArray = [...oldSizeData, ...addedSizeData] || [];
        const formattedString = newColorArray.map(item => `${item.color}${item.additionalSalePrice}`).join('') + newSizeArray.map(item => `${item.size}${item.additionalSalePrice}`).join('');
        return formattedString;
    }

    const initialValues = {
        categoryId: productData?.categoryId || '',
        brandName: productData?.brandName || '',
        productName: productData?.productName || '',
        salePrice: productData?.salePrice || "",
        multiColorFlag: productData?.multiColorFlag || false,
        multiSizeFlag: productData?.multiSizeFlag || false,
        productPicture: "",
        notShowInDashboardFlag: productData?.notShowInDashboardFlag || false,
        postedLink: productData?.postedLink || "",
        comment: productData?.comment || "",
        colorSizeChangeDirtyTrigger: getColorSizeMemoryData(oldColorList, colorList, oldSizeList, sizeList) || ""
    };

    const validationSchema = Yup.object({
        categoryId: Yup.string().required('Category Name is required.'),
        brandName: Yup.string()
            .max(30, 'Brand Name must not exceed 30 characters.')
            .required('Brand Name is required.'),
        productName: Yup.string()
            .max(30, 'Product Name must not exceed 30 characters.')
            .required('Product Name is required.'),
        salePrice: Yup.number().nullable()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Sale Price must exceed 1¥.')
            .max(1000000, 'Sale Price must not exceed 1000000¥.')
            .when([], {
                is: () => productData?.totalPurchasedQuantity > 0,
                then: (schema) => schema.required("Sale Price is required."),
                otherwise: (schema) => schema
            }),
        multiColorFlag: Yup.boolean(),
        multiSizeFlag: Yup.boolean(),
        productPicture: Yup.mixed().nullable()
            .test(
                'fileType',
                'Only JPG, PNG, and JPEG files are allowed.',
                (value) => {
                    if (!value) return true;
                    return value && value.type.startsWith('image/');
                    // return value && ['image/jpeg', 'image/png', 'image/jpg'].includes(value.type);
                }
            ),
            // .test(
            //     'fileSize',
            //     'Image size must be less than 2MB.',
            //     (value) => {
            //         if (!value) return true;
            //         return value && value.size <= 2 * 1024 * 1024; // 2MB size limit
            //     }
            // ),
        notShowInDashboardFlag: Yup.boolean(),
        postedLink: Yup.string().nullable()
            .max(500, 'Link must not exceed 500 characters.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.'),
        colorSizeChangeDirtyTrigger: Yup.string().nullable()
    });

    const onSubmit = async (form) => {
        setLoading(true);
        try {
            // setShowSuccessDialog(true);
            setFormValue(form);
            setShowConfirmDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDialogClose = () => {
        if ((parseInt(productId) || 0) > 0) {
            navigate(`${ROUTE_PRODUCT_DETAILS}/${productId}`);
        } else {
            navigate(ROUTE_PRODUCT_LIST);
        }
    }

    const handleEditProduct = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const formData = {
                productId: productData?.productId,
                categoryId: parseInt(formValue?.categoryId) || 0,
                brandName: formValue?.brandName,
                productName: formValue?.productName,
                salePrice: parseInt(formValue?.salePrice) || 0,
                productPicture: formValue?.productPicture,
                notShowInDashboardFlag: formValue?.notShowInDashboardFlag,
                postedLink: formValue?.postedLink,
                comment: formValue?.comment,
                colorList: colorList,
                sizeList: sizeList,
                editedColorList: editedColorList,
                editedSizeList: editedSizeList
            }
            const response = await editProduct(formData);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleAddColorClick = (setFieldValue, setFieldTouched) => {
        if (isNullOrEmptyString(colorName)) {
            return;
        }
        if (oldColorList?.find(data => data.color?.trim() === colorName?.trim())) {
            setAlertMessage(`Color [${colorName}] is already exist.`);
            return;
        }
        setColorList(prev => {
            const filteredData = (prev || []).filter(data => data?.color?.trim() !== colorName?.trim());
            const additionalSalePrice = isNaN(parseInt(additionalColorPrice)) || parseInt(additionalColorPrice) === -0 ? 0 : parseInt(additionalColorPrice);
            const updatedData = [
                ...filteredData,
                {
                    color: colorName?.trim(),
                    additionalSalePrice: additionalSalePrice
                }
            ]
            setColorName("");
            setAdditionalColorPrice("");
            setTimeout(() => {
                setTimeout(() => {
                    setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(oldColorList, updatedData, oldSizeList, sizeList));
                }, 100);
                setFieldTouched('multiColorFlag');
            }, 100);
            return updatedData;
        });
    }

    const handleAddSizeClick = (setFieldValue, setFieldTouched) => {
        if (isNullOrEmptyString(size)) {
            return;
        }
        if (oldSizeList?.find(data => data.size?.trim() === size?.trim())) {
            setAlertMessage(`Size [${size}] is already exist.`);
            return;
        }
        setSizeList(prev => {
            const filteredData = (prev || []).filter(data => data?.size?.trim() !== size?.trim());
            const additionalSalePrice = isNaN(parseInt(additionalSizePrice)) || parseInt(additionalSizePrice) === -0 ? 0 : parseInt(additionalSizePrice);
            const updatedData = [
                ...filteredData,
                {
                    size: size?.trim(),
                    additionalSalePrice: additionalSalePrice
                }
            ]
            setSize("");
            setAdditionalSizePrice("");
            setTimeout(() => {
                setTimeout(() => {
                    setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(oldColorList, colorList, oldSizeList, updatedData));
                }, 100);
                setFieldTouched('multiSizeFlag');
            }, 100);
            return updatedData;
        });
    }

    const handleImageChange = (e, setFieldValue) => {
        const file = e.target.files[0];
        if (file) {
            setImagePreview(URL.createObjectURL(file));
            setFieldValue('productPicture', file);
        } else {
            setImagePreview(null);
            setFieldValue("productPicture", "");
        }
    };

    const handleEditColorClick = (setFieldValue) => {
        const editColorId = parseInt(currentEditColorId) || 0;
        if (editColorId <= 0) {
            resetColorDataInput();
            return;
        }
        const updatedColor = {
            colorId: editColorId,
            color: colorName?.trim(),
            additionalSalePrice: parseInt(additionalColorPrice) || 0
        };

        // Update oldColorList (ensure immutability)
        setOldColorList(prev => {
            const updatedData = prev.map(c => (c.colorId === editColorId ? { ...c, ...updatedColor } : c));
            setTimeout(() => {
                setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(updatedData, colorList, oldSizeList, sizeList));
            }, 100);
            return updatedData;
        });

        // Update editedColorList (ensure color is replaced or added)
        setEditedColorList(prev => {
            const exists = prev.some(c => c.colorId === editColorId);

            const updatedData = exists ? prev.map(c => (c.colorId === editColorId ? { ...c, ...updatedColor } : c))
                : [...prev, updatedColor];
            return updatedData;
        });
        resetColorDataInput();
    }

    const resetColorDataInput = () => {
        setCurrentEditColorId(null);
        setColorName('');
        setAdditionalColorPrice('');
    }

    const resetSizeDataInput = () => {
        setCurrentEditSizeId(null);
        setSize('');
        setAdditionalSizePrice('');
    }

    const handleEditSizeClick = (setFieldValue) => {
        const editSizeId = parseInt(currentEditSizeId) || 0;
        if (editSizeId <= 0) {
            resetSizeDataInput();
            return;
        }

        const updatedSize = {
            sizeId: editSizeId,
            size: size?.trim(),
            additionalSalePrice: parseInt(additionalSizePrice) || 0
        };

        // Update oldSizeList (ensure immutability)
        setOldSizeList(prev => {
            const updatedData = prev.map(c => (c.sizeId === editSizeId ? { ...c, ...updatedSize } : c));
            setTimeout(() => {
                setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(oldColorList, colorList, updatedData, sizeList));
            }, 100);
            return updatedData;
        });

        // Update editedSizeList (ensure color is replaced or added)
        setEditedSizeList(prev => {
            const exists = prev.some(c => c.sizeId === editSizeId);
            const updatedData = exists
                ? prev.map(c => (c.sizeId === editSizeId ? { ...c, ...updatedSize } : c))
                : [...prev, updatedSize];

            return updatedData
        });
        resetSizeDataInput();
    }

    return (
        <div id='createProductPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"Edit Product"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        validate={(values) => {
                                            const errors = {};
                                            if (productData?.multiColorFlag !== true && oldColorList?.length < 2 && values.multiColorFlag && colorList.length <= 1) {
                                                errors.multiColorFlag = 'Add at least two colors.';
                                            }
                                            if (productData?.multiSizeFlag !== true && oldSizeList?.length < 2 && values.multiSizeFlag && sizeList.length <= 1) {
                                                errors.multiSizeFlag = 'Add at least two sizes.';
                                            }
                                            return errors;
                                        }}
                                        onSubmit={onSubmit}

                                    >
                                        {({ values, setFieldTouched, setFieldValue, errors, isValid, dirty }) => {
                                            // const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                                            return (
                                                <Form>
                                                    <div className="mt-1">
                                                        <ErrorMessage name="colorSizeChangeDirtyTrigger" component="div" className="formErrDiv text-danger" />
                                                    </div>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Category Name*</label>
                                                        <Field as="select" name="categoryId" className="w-100 customInputControl">
                                                            <option value="" disabled>Select Category</option>
                                                            {categories.map(category => (
                                                                <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>
                                                            ))}
                                                        </Field>
                                                        <ErrorMessage name="categoryId" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Brand Name*</label>
                                                        <Field name="brandName" type="text" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="brandName" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Product Name*</label>
                                                        <Field name="productName" type="text" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="productName" component="div" className="formErrDiv text-danger" />
                                                    </div>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Total Purchased Quantity</label>
                                                        <div className="w-100 label-box">
                                                            {productData?.totalPurchasedQuantity || 0}
                                                        </div>
                                                    </div>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Total Purchased Price</label>
                                                        <div className="w-100 label-box">
                                                            {productData?.totalPurchasedAmount || 0}¥
                                                        </div>
                                                    </div>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Price Per Item</label>
                                                        <div className="w-100 label-box">
                                                            {pricePerItemState || 0}¥
                                                        </div>
                                                    </div>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Suggested Sale Price ({suggestedSalePercentage}%)</label>
                                                        <div className="w-100 label-box">
                                                            {suggestedSalePrice || 0}¥
                                                        </div>
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Sale Price{productData?.totalPurchasedQuantity > 0 ? "*" : ""}</label>
                                                        <Field name="salePrice"
                                                            onChange={(e) => {
                                                                setSalePriceState(e.target.value);
                                                                setFieldValue("salePrice", e.target.value);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                    e.preventDefault(); // Prevent form submission
                                                                }
                                                            }}
                                                            onWheel={(e) => e.target.blur()}
                                                            type="number"
                                                            inputMode="numeric"  // forces numeric keypad on mobile
                                                            pattern="[0-9]*"      // enforces only digits
                                                            className="w-100 customInputControl clearNumberSpin" />
                                                        <ErrorMessage name="salePrice" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Estimate Profit</label>
                                                        <div className="w-100 label-box main-description">
                                                            {estimateProfit || 0}¥
                                                        </div>
                                                    </div>

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Multiple Colors
                                                            <Field type="checkbox" name="multiColorFlag" className="form-check-input ms-2" disabled={true} />
                                                        </label>
                                                        <ErrorMessage name="multiColorFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    {(productData?.multiColorFlag === true || values?.multiColorFlag === true) &&
                                                        <div className="mb-1 colorSizeBlockSection">
                                                            {(oldColorList.length > 0 || colorList.length > 0) && <div className='colorSizeBlockWrapper mb-2'>
                                                                {oldColorList.map((data, indexToEdit) => (
                                                                    <span key={indexToEdit} className='colorSizeBlock'>{data.color}{` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)`}
                                                                        {(!currentEditColorId || currentEditColorId === data.colorId) && <button type='button' className='icon-btn p-0 ms-2 text-primary'
                                                                            onClick={() => {
                                                                                setCurrentEditColorId(data.colorId);
                                                                                setColorName(data.color);
                                                                                setAdditionalColorPrice(data.additionalSalePrice)
                                                                            }}>
                                                                            <i className="bi bi-pencil-square"></i>
                                                                        </button>}
                                                                    </span>
                                                                ))}
                                                                {colorList.map((data, indexToRemove) => (
                                                                    <span key={indexToRemove} className='colorSizeBlock'>{data.color}{` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)`}
                                                                        {!currentEditColorId && <button type='button' className='icon-btn p-0 ms-2 text-danger'
                                                                            onClick={() => {
                                                                                setColorList(prev => {
                                                                                    const updatedData = prev.filter((_, index) => index !== indexToRemove)
                                                                                    setTimeout(() => {
                                                                                        setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(oldColorList, updatedData, oldSizeList, sizeList));
                                                                                        setTimeout(() => {
                                                                                            setFieldTouched("multiColorFlag")
                                                                                        }, 100);
                                                                                    }, 100);
                                                                                    return updatedData;
                                                                                });
                                                                            }}>
                                                                            <i className="bi bi-x-circle-fill"></i></button>}
                                                                    </span>
                                                                ))}
                                                            </div>}

                                                            <div className='row mb-1'>
                                                                <div className='col-md-4'>
                                                                    <label className="custom-label">Color</label>
                                                                </div>
                                                                <div className='col-md-8'>
                                                                    <input value={colorName} type="text" className="w-100 customInputControl" autoComplete="off"
                                                                        // disabled={!!currentEditColorId}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                e.preventDefault(); // Prevent form submission
                                                                            }
                                                                        }}
                                                                        onChange={(e) => setColorName(e.target.value)} />
                                                                </div>
                                                            </div>
                                                            <div className='row mb-1'>
                                                                <div className='col-md-4'>
                                                                    <label className="custom-label">Additional Price</label>
                                                                </div>
                                                                <div className='col-md-8'>
                                                                    <input value={additionalColorPrice} type="number"
                                                                        inputMode="numeric"  // forces numeric keypad on mobile
                                                                        pattern="[0-9]*"      // enforces only digits
                                                                        className="w-100 customInputControl clearNumberSpin"
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter" || e.key === "e" || e.key === "." || e.key === "+") {
                                                                                e.preventDefault(); // Prevent form submission
                                                                            }
                                                                        }}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        onChange={(e) => setAdditionalColorPrice(e.target.value)} />
                                                                    {!currentEditColorId ? <div>
                                                                        <Button type='button' variant='success' className='appBtn mt-2' disabled={isNullOrEmptyString(colorName)}
                                                                            onClick={() => handleAddColorClick(setFieldValue, setFieldTouched)} >Add Color</Button>
                                                                    </div> :
                                                                        <div className='mt-2' style={{
                                                                            display: 'flex', justifyContent: 'start', gap: '20px'
                                                                        }}>
                                                                            <Button type='button' variant='success' className='appBtn' disabled={isNullOrEmptyString(colorName)}
                                                                                onClick={() => handleEditColorClick(setFieldValue, setFieldTouched)} >Edit Color</Button>
                                                                            <Button type='button' variant='secondary' className='appBtn' onClick={() => {
                                                                                resetColorDataInput();
                                                                            }} >Cancel</Button>
                                                                        </div>}
                                                                </div>
                                                            </div>
                                                        </div>}

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Multiple Sizes
                                                            <Field type="checkbox" name="multiSizeFlag" className="form-check-input ms-2" disabled={true} />
                                                        </label>
                                                        <ErrorMessage name="multiSizeFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>
                                                    {(productData?.multiSizeFlag === true || values?.multiSizeFlag === true) && <div className="mb-1 colorSizeBlockSection">

                                                        {(oldSizeList.length > 0 || sizeList.length > 0) && <div className='colorSizeBlockWrapper mb-2'>
                                                            {oldSizeList.map((data, indexToEdit) => (
                                                                <span key={indexToEdit} className='colorSizeBlock'>{data.size}{` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)`}
                                                                    {(!currentEditSizeId || currentEditSizeId === data.sizeId) && <button type='button' className='icon-btn p-0 ms-2 text-primary'
                                                                        onClick={() => {
                                                                            setCurrentEditSizeId(data.sizeId);
                                                                            setSize(data.size);
                                                                            setAdditionalSizePrice(data.additionalSalePrice)
                                                                        }}>
                                                                        <i className="bi bi-pencil-square"></i>
                                                                    </button>}
                                                                </span>
                                                            ))}
                                                            {sizeList.map((data, indexToRemove) => (
                                                                <span key={indexToRemove} className='colorSizeBlock'>{data.size}{` (${data.additionalSalePrice > 0 ? '+' : ''}${data.additionalSalePrice}¥)`}
                                                                    {!currentEditSizeId && <button type='button' className='icon-btn p-0 ms-2 text-danger'
                                                                        onClick={() => {
                                                                            setSizeList(prev => {
                                                                                const updatedData = prev.filter((_, index) => index !== indexToRemove);
                                                                                setTimeout(() => {
                                                                                    setFieldValue("colorSizeChangeDirtyTrigger", getColorSizeMemoryData(oldColorList, colorList, oldSizeList, updatedData));
                                                                                    setTimeout(() => {
                                                                                        setFieldTouched("multiSizeFlag")
                                                                                    }, 100);
                                                                                }, 100);
                                                                                return updatedData;
                                                                            });
                                                                        }}>
                                                                        <i className="bi bi-x-circle-fill"></i></button>}
                                                                </span>
                                                            ))}
                                                        </div>}

                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Size</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={size} type="text" className="w-100 customInputControl" autoComplete="off"
                                                                    // disabled={!!currentEditSizeId}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onChange={(e) => setSize(e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Additional Price</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={additionalSizePrice} type="number"
                                                                    inputMode="numeric"  // forces numeric keypad on mobile
                                                                    pattern="[0-9]*"      // enforces only digits
                                                                    className="w-100 customInputControl clearNumberSpin"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === "e" || e.key === "." || e.key === "+") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    onChange={(e) => setAdditionalSizePrice(e.target.value)} />

                                                                {!currentEditSizeId && <div>
                                                                    <Button type='button' variant='success' className='appBtn mt-2' disabled={isNullOrEmptyString(size)}
                                                                        onClick={() => handleAddSizeClick(setFieldValue, setFieldTouched)} >Add Size</Button>
                                                                </div>}
                                                                {currentEditSizeId && <div className='mt-2' style={{
                                                                    display: 'flex', justifyContent: 'start', gap: '20px'
                                                                }}>
                                                                    <Button type='button' variant='success' className='appBtn' disabled={isNullOrEmptyString(size)}
                                                                        onClick={() => handleEditSizeClick(setFieldValue)} >Edit Size</Button>
                                                                    <Button type='button' variant='secondary' className='appBtn' onClick={() => {
                                                                        resetSizeDataInput();
                                                                    }} >Cancel</Button>
                                                                </div>}
                                                            </div>
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Old Product Picture</label>
                                                        <div className="">
                                                            <img
                                                                src={productData?.productPicture ? `data:image/jpeg;base64,${productData?.productPicture}` : noImage}
                                                                alt="Preview"
                                                                className="img-thumbnail"
                                                                style={{ width: "var(--productPictureWidth)", height: "100px", objectFit: "cover" }}
                                                            />
                                                        </div>

                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">New Product Picture</label>
                                                        <Field name="productPicture">
                                                            {({ field, form }) => (
                                                                <div>
                                                                    <input
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        className="w-100 customInputControl"
                                                                        onChange={(e) => handleImageChange(e, setFieldValue)}
                                                                    />
                                                                    {imagePreview && (
                                                                        <div className="mt-2">
                                                                            <img
                                                                                src={imagePreview}
                                                                                alt="Preview"
                                                                                className="img-thumbnail"
                                                                                style={{ width: "var(--productPictureWidth)", height: "100px", objectFit: "cover" }}
                                                                            />
                                                                            <button
                                                                                style={{
                                                                                    color: 'red'
                                                                                }}
                                                                                type="button"
                                                                                className="icon-btn"
                                                                                onClick={() => {
                                                                                    form.setFieldValue('productPicture', ''); // Reset Formik value
                                                                                    setImagePreview(null);
                                                                                    if (fileInputRef.current) {
                                                                                        fileInputRef.current.value = ""; // Clear input field
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-x-circle-fill"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Field>
                                                        <ErrorMessage name="productPicture" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Posted Link</label>
                                                        <Field name="postedLink" component="textarea" rows="3" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="postedLink" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Not Show In Dashboard
                                                            <Field type="checkbox" name="notShowInDashboardFlag" className="form-check-input ms-2" />
                                                        </label>
                                                        <ErrorMessage name="notShowInDashboardFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Comment</label>
                                                        <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className='floating-bottom-btn-group'>
                                                        <div className='floating-bottom-btn-grid'>
                                                            <Button
                                                                className="w-100"
                                                                type="submit"
                                                                variant="primary"
                                                                onClick={scrollToFormError}
                                                                disabled={!dirty}
                                                            >
                                                                Change
                                                            </Button>

                                                            {/* <Button
                                                            className="w-100 mt-2"
                                                            type="button"
                                                            variant="secondary"
                                                            onClick={() => navigate(ROUTE_PRODUCT_LIST)}
                                                        >
                                                            Go to Product List
                                                        </Button>
                                                        <Button
                                                            className="w-100 mt-2"
                                                            type="button"
                                                            variant="secondary"
                                                            onClick={() => navigate(`${ROUTE_PRODUCT_DETAILS}/${productData?.productId}`)}
                                                        >
                                                            Product Details
                                                        </Button> */}
                                                        </div>
                                                    </div>
                                                </Form>
                                            );
                                        }}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='productEditConfirmDialog'
                    showDialog={showConfirmDialog}
                    // title='Logout Dialog'
                    body='Are you sure to edit the product data?'
                    onConfirm={handleEditProduct}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'alertDialog'}
                    showDialog={!isNullOrEmptyString(alertMessage)}
                    body={alertMessage}
                    onConfirm={() => setAlertMessage(null)}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Product has been successfully updated.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
}

export default ProductEdit;